import { CornerDownRight, Eye, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMessagePreview, type MessagePreview } from "@/lib/message-preview";

/**
 * Bảng biến điền tự động. Bấm một biến là chèn thẳng vào nội dung — người dùng
 * không phải nhớ tên biến hay gõ đúng dấu ngoặc.
 */
export function MessageVariables({
    accountId,
    text,
    onInsert,
}: {
    accountId: string;
    text: string;
    onInsert: (token: string) => void;
}) {
    const preview = useMessagePreview();
    const data: MessagePreview | undefined = preview.data;
    const variables = data?.variables ?? FALLBACK_VARIABLES;

    return (
        <div className="space-y-2">
            <div className="rounded-lg border border-line bg-surface-muted px-3 py-2.5">
                <p className="mb-2 text-xs text-ink-muted">
                    Bấm để chèn biến — khi gửi, mỗi nhóm sẽ được điền thông tin của chính nhóm đó.
                </p>
                <div className="flex flex-wrap gap-1.5">
                    {variables.map((variable) => (
                        <button
                            key={variable.key}
                            type="button"
                            onClick={() => onInsert(`{{${variable.key}}}`)}
                            title={`Ví dụ: ${variable.example}`}
                            className="rounded-md border border-line bg-surface px-2 py-1 font-mono text-xs text-ink-muted transition hover:border-brand hover:text-brand"
                        >
                            {variable.label}
                        </button>
                    ))}
                </div>
            </div>

            <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={preview.isPending}
                disabled={!text.trim()}
                onClick={() => preview.mutate({ text, accountId })}
            >
                <Eye className="size-4" />
                Xem thử với nhóm thật
            </Button>

            {data && text.trim() && (
                <div className="space-y-1.5 rounded-lg bg-surface-muted px-3 py-2 text-xs">
                    {data.unknownVariables.length > 0 && (
                        <p className="flex items-start gap-1.5 text-danger">
                            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                            Biến không tồn tại: {data.unknownVariables.map((key) => `{{${key}}}`).join(", ")} —
                            sẽ được gửi nguyên văn như vậy
                        </p>
                    )}

                    {data.samples.map((sample) => (
                        <div key={sample.groupName} className="flex items-start gap-1.5">
                            <CornerDownRight className="mt-0.5 size-3 shrink-0 text-ink-muted" aria-hidden />
                            <div className="min-w-0">
                                <p className="truncate font-medium text-ink-muted">{sample.groupName}</p>
                                <p className="whitespace-pre-wrap text-ink">{sample.rendered}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Dùng trước khi gọi API lần đầu, để bảng biến hiện ngay chứ không trống trơn. */
const FALLBACK_VARIABLES = [
    { key: "ten_nhom", label: "Tên nhóm", example: "Khách sỉ Hà Nội" },
    { key: "so_thanh_vien", label: "Số thành viên", example: "120" },
    { key: "ten_toi", label: "Tên tài khoản gửi", example: "Nguyễn Văn A" },
    { key: "ngay", label: "Ngày gửi", example: "10/08/2026" },
    { key: "thu", label: "Thứ trong tuần", example: "Thứ Hai" },
    { key: "gio", label: "Giờ gửi", example: "09:30" },
    { key: "thang", label: "Tháng", example: "8" },
    { key: "nam", label: "Năm", example: "2026" },
];
